import { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Switch,
  ButtonGroup,
  IconButton,
  Text,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Flex,
  Button,
  AspectRatio,
  Tooltip,
  Skeleton,
  Center,
} from "@chakra-ui/react";
import {
  RiVideoLine,
  RiArrowRightLine,
  RiExternalLinkLine,
} from "react-icons/ri";
import MuxPlayer from "@mux/mux-player-react";
import { Link, useNavigate } from "react-router-dom";
import { SystemChecklistItemEntity } from "../../../shared/_core/system-checklist.types";
import DashboardWidget from "./DashboardWidget";
import { api } from "../../../utilities/apiRequest";
import useToast from "../../../utilities/useToast";
import StartATaskModal from "../../chakra/navigation/StartATaskModal";
import Spinner from "../../chakra/common/Spinner";
import { DashboardSettings } from "../../../utils/settings";

type Props = {
  title?: string;
  isMinimized: boolean;
  toggleMinimize: () => void;
};

const ChecklistWidget = ({ title, isMinimized, toggleMinimize }: Props) => {
  const checklistQuery = api.systemChecklist.list.useQuery({});
  const updateChecklistMutation = api.systemChecklist.update.useMutation({});
  const { displayToast } = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const startATaskModal = useDisclosure();
  const [currentDoc, setCurrentDoc] = useState<string>("");
  const [showAll, setShowAll] = useState<boolean>(true);
  const [manualOverride, setManualOverride] = useState(false);
  const [localData, setLocalData] = useState<SystemChecklistItemEntity[]>([]);
  const [videoLoading, isVideoLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  const allItemsCompleted =
    localData.length > 0 && localData.every((item) => item.isCompleted);
  const navigate = useNavigate();
  useEffect(() => {
    if (checklistQuery.data?.items) {
      setLocalData(checklistQuery.data.items);
    }
  }, [checklistQuery.data?.items]);

  useEffect(() => {
    if (manualOverride) return;
    if (localData.length === 0 && checklistQuery.isLoading) return;
    if (localData.length === 0) return;

    if (allItemsCompleted && !isMinimized) {
      toggleMinimize();
      DashboardSettings.setGetStartedMinimized(true);
    } else if (!allItemsCompleted && isMinimized && !hasInitialized) {
      toggleMinimize();
    }

    if (!hasInitialized) {
      setHasInitialized(true);
    }
  }, [
    allItemsCompleted,
    isMinimized,
    manualOverride,
    toggleMinimize,
    localData.length,
    checklistQuery.isLoading,
    hasInitialized,
  ]);

  const handleChecklistUpdate = async (id: string, isComplete: boolean) => {
    try {
      await updateChecklistMutation.mutateAsync({
        checklistCode: id,
        completed: isComplete,
      });
    } catch (error) {
      displayToast({
        title: "Error updating checklist",
        description: "There was an error updating the checklist.",
        status: "error",
        isClosable: true,
      });
    }
  };

  const onToggle = (id: string) => {
    const checklistItem = localData.find((item) => item.checklistCode === id);
    if (!checklistItem) return;

    const newCompletedState = !checklistItem.isCompleted;

    const wasAllCompleted = localData.every((item) => item.isCompleted);
    const willBeAllCompleted = localData.every((item) =>
      item.checklistCode === id ? newCompletedState : item.isCompleted,
    );

    // Update local data
    setLocalData((prevData) =>
      prevData.map((item) =>
        item.checklistCode === id
          ? { ...item, isCompleted: newCompletedState }
          : item,
      ),
    );

    if (wasAllCompleted && !willBeAllCompleted && isMinimized) {
      toggleMinimize();
      DashboardSettings.setGetStartedMinimized(false);
    }

    handleChecklistUpdate(checklistItem.checklistCode, newCompletedState);
    setManualOverride(false);
  };

  const handleOpenModal = (link: string) => {
    isVideoLoading(true);
    setCurrentDoc(link);
    onOpen();
  };
  const handleOpenRecordModal = (link: string) => {
    if (link === "start-a-task") {
      startATaskModal.onOpen();
    }
  };

  const handleManualMinimize = () => {
    const newMinimizedState = !isMinimized;
    toggleMinimize();
    DashboardSettings.setGetStartedMinimized(newMinimizedState);
    setManualOverride(true);
  };

  const sortedData = [...localData].sort((a, b) =>
    a.isCompleted === b.isCompleted ? 0 : a.isCompleted ? 1 : -1,
  );
  const visibleItems = showAll
    ? sortedData
    : sortedData.filter((item) => !item.isCompleted);

  const renderSkeletons = () =>
    Array.from({ length: 3 }).map((_, index) => (
      <Box key={index} borderRadius="lg">
        <Skeleton height="8" w="full" />
      </Box>
    ));

  const renderCheckListItems = () =>
    visibleItems.length > 0 ? (
      visibleItems.map((item, index, arr) => {
        const { isCompleted } = item;
        const prevItem = arr[index - 1];

        const shouldInsertDivider =
          index > 0 && prevItem && !prevItem.isCompleted && isCompleted;

        const isFirstUnticked =
          !isCompleted &&
          (index === 0 || (arr[index - 1] && arr[index - 1]?.isCompleted)) &&
          showAll;

        return (
          <Box key={item.checklistCode}>
            {isFirstUnticked && (
              <Box>
                <Text fontSize="sm" my={2}>
                  To Do
                </Text>
              </Box>
            )}
            {shouldInsertDivider && (
              <Box py={2}>
                <Divider />
                <Text fontSize="sm" my={2}>
                  Completed Items
                </Text>
              </Box>
            )}
            <Flex
              align="center"
              justify="space-between"
              p={2}
              borderWidth={1}
              borderRadius="lg"
              bg={isCompleted ? "green.50" : "white"}
              borderColor={isCompleted ? "green.300" : "gray.200"}>
              <HStack spacing={2}>
                <Switch
                  isChecked={isCompleted}
                  onChange={() => onToggle(item.checklistCode)}
                  colorScheme="green"
                  size="md"
                />
                <Box>
                  <Text
                    fontSize="md"
                    fontWeight="semibold"
                    textDecoration={isCompleted ? "line-through" : "none"}
                    color={isCompleted ? "green.700" : "black"}>
                    {item.order}: {item.action} {item.name}
                  </Text>
                  <Text
                    fontSize="sm"
                    color={isCompleted ? "green.600" : "gray.600"}
                    textDecoration={isCompleted ? "line-through" : "none"}>
                    {item.description}
                  </Text>
                </Box>
              </HStack>
              <ButtonGroup isAttached size="lg" variant="ghost">
                {item.helpVideoLink !== "" && (
                  <Tooltip label="View Help Video" placement="top" hasArrow>
                    <IconButton
                      icon={<RiVideoLine />}
                      aria-label="View Help Video"
                      onClick={() => handleOpenModal(item.helpVideoLink)}
                      variant="ghost"
                      colorScheme={isCompleted ? "green" : undefined}
                    />
                  </Tooltip>
                )}

                {item.helpDocumentLink !== "" && (
                  <Tooltip label="View Help Document" placement="top" hasArrow>
                    <IconButton
                      icon={<RiExternalLinkLine />}
                      aria-label="View Help Doc"
                      onClick={() =>
                        window.open(
                          `https://help.certcrowd.com/docs/${item.helpDocumentLink}`,
                          "_blank",
                        )
                      }
                      variant="ghost"
                      colorScheme={isCompleted ? "green" : undefined}
                    />
                  </Tooltip>
                )}

                <Tooltip label={`Go to ${item.name}`} placement="top" hasArrow>
                  {item.recordLink.startsWith("modal/") ? (
                    <IconButton
                      icon={<RiArrowRightLine />}
                      aria-label="View Record"
                      onClick={() =>
                        handleOpenRecordModal(
                          item.recordLink.replace("modal/", ""),
                        )
                      }
                      variant="ghost"
                      colorScheme={isCompleted ? "green" : undefined}
                    />
                  ) : item.recordLink !== "" ? (
                    <IconButton
                      icon={<RiArrowRightLine />}
                      aria-label="View Record"
                      onClick={() => navigate(`/${item.recordLink}`)}
                      variant="ghost"
                      colorScheme={isCompleted ? "green" : undefined}
                    />
                  ) : null}
                </Tooltip>
              </ButtonGroup>
            </Flex>
          </Box>
        );
      })
    ) : (
      <Box p={4} textAlign="center">
        <Text fontSize="sm" color="gray.500">
          You have completed all items in this checklist
        </Text>
      </Box>
    );

  return (
    <DashboardWidget
      isLoading={checklistQuery.isLoading}
      isRefetching={checklistQuery.isRefetching}
      onReload={checklistQuery.refetch}
      canMinimize
      title={title ?? "Get Started Now"}
      helpText="Get started now checklist for new users."
      isMinimized={isMinimized}
      toggleMinimize={handleManualMinimize}>
      <Box h="full">
        <VStack spacing={2} align="stretch" maxH="300px" overflowY="auto">
          {checklistQuery.isLoading || checklistQuery.isFetching
            ? renderSkeletons()
            : renderCheckListItems()}
        </VStack>

        <Box mt={4} px={2} width="full" display="flex" justifyContent="center">
          <Button
            size="sm"
            onClick={() => setShowAll((prev) => !prev)}
            variant="ghost"
            colorScheme="blue">
            {showAll ? "Hide Completed" : "Show Completed"}
          </Button>
        </Box>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <chakra-scope>
            <ModalHeader>Help Video</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <AspectRatio ratio={16 / 12}>
                <>
                  {videoLoading && (
                    <Center height={100}>
                      <Spinner
                        opacity={1}
                        size="lg"
                        position="absolute"
                        left="50%"
                      />
                    </Center>
                  )}
                  <iframe
                    title="Help Video"
                    loading="lazy"
                    onLoad={() => isVideoLoading(false)}
                    src={`https://scribehow.com/embed/${currentDoc}?removeLogo=true&as=video`}
                    allowFullScreen
                  />
                </>
              </AspectRatio>
            </ModalBody>
          </chakra-scope>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={startATaskModal.isOpen}
        onClose={startATaskModal.onClose}
        size="sm">
        <ModalOverlay />
        <StartATaskModal onClose={startATaskModal.onClose} />
      </Modal>
    </DashboardWidget>
  );
};

export default ChecklistWidget;
